<template>
    <div id="invoices-page">
        <div class="container-fluid py-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
                <h1 class="h3 mb-0 text-gray-800">{{ t('invoices-title') }}</h1>
            </div>

            <div v-if="filtersReady" class="mb-3 invoices-filter-bar">
                <KvFilter
                    :defaultSelected="['period']"
                    ref="filterBar"
                    @changed="updateFilters"
                    :show-labels="true"
                    :has-period-filter="false"
                    :has-branch-filter="false"
                    :filters="extraFilters"
                />
                <div class="clearfix"></div>
            </div>

            <invoice-summary-cards :summary="summary" />

            <div class="d-flex justify-content-end align-items-center flex-wrap gap-sm mb-4 mt-1 invoices-actions-bar">
                <kv-button
                    v-if="isAdmin"
                    variant="outlined"
                    prepend-icon="ui-refresh-cw-02"
                    :loading="monthlyGenerationLoading"
                    :disabled="monthlyGenerationExecuting"
                    @click="openMonthlyGenerationPreview"
                >
                    {{ t('invoices-generate_monthly') }}
                </kv-button>
                <kv-button
                    v-if="isAdmin"
                    variant="outlined"
                    prepend-icon="ui-download-01"
                    :loading="exportLoading"
                    :disabled="monthlyGenerationExecuting"
                    @click="exportInvoices"
                >
                    {{ t('invoices-export') }}
                </kv-button>
                <kv-button variant="filled" prepend-icon="ui-plus" @click="$refs.chargeModal.open()">
                    {{ t('invoices-add_charge') }}
                </kv-button>
            </div>

            <KvModal ref="chargeModal" id="add-charge-modal" size="large" @close="() => $refs.chargeForm && $refs.chargeForm.reset()">
                <template #title>{{ t('invoices-add_charge') }}</template>
                <template #subtitle>{{ t('invoices-add_charge_subtitle') }}</template>
                <template #body>
                    <add-charge-form 
                        ref="chargeForm"
                        @success="onChargesCreated" 
                        @cancel="$refs.chargeModal.close()" 
                    />
                </template>
            </KvModal>

            <div class="row main-row">
                <div class="col-12">
                    <KvCard class="invoices-grid-card">
                        <template #text>
                            <div class="table-responsive invoices-grid-wrapper">
                                <grid
                                    :source="'invoices/admin'"
                                    :filters="filters"
                                    :showSearch="false"
                                    ref="invoicesGrid"
                                >
                                    <template v-slot:rows="grid">
                                        <thead>
                                            <tr>
                                                <grid-th :grid="grid" attr="title" :sortable="true">{{ t('invoices-invoice') }}</grid-th>
                                                <grid-th :grid="grid" attr="place_name" :sortable="true">{{ t('invoices-establishment') }}</grid-th>
                                                <grid-th :grid="grid" attr="created_at" :sortable="true">{{ t('invoices-issue_date') }}</grid-th>
                                                <grid-th :grid="grid" attr="due_date" :sortable="true">{{ t('invoices-due_date') }}</grid-th>
                                                <grid-th :grid="grid" attr="total" :sortable="true">{{ t('invoices-total_amount') }}</grid-th>
                                                <grid-th :grid="grid" attr="status" :sortable="true">{{ t('invoices-status_label') }}</grid-th>
                                                <grid-th :grid="grid" class="text-left">{{ t('invoices-actions') }}</grid-th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="invoice in (grid.data || [])" :key="invoice.id">
                                                <td class="text-nowrap">
                                                    <span class="font-weight-500 text-dark">{{ invoice.title || invoice.id }}</span>
                                                </td>
                                                <td class="est-col">
                                                    <div class="font-weight-500">{{ invoice.place_name || `${t('invoices-establishment')} #${invoice.places_id}` }}</div>
                                                </td>
                                                <td class="text-nowrap">
                                                    <human-date :date="invoice.created_at" format="[d]/[M]/[Y]" />
                                                </td>
                                                <td class="text-nowrap">
                                                    <human-date :date="invoice.due_date" format="[d]/[M]/[Y]" />
                                                </td>
                                                <td class="text-nowrap">
                                                    <div class="font-weight-500">{{ formatCurrency(invoice.total) }}</div>
                                                    <div v-if="invoice.total_paid !== null && Number(invoice.total_paid).toFixed(2) !== Number(invoice.total).toFixed(2)"
                                                         class="mt-1" style="font-size: 11px; color: #6B7280;">
                                                        <span class="text-success font-weight-500 mr-1">{{ t('invoices-paid_label') }}</span>
                                                        {{ formatCurrency(invoice.total_paid) }}
                                                    </div>
                                                </td>
                                                <td class="text-nowrap">
                                                    <invoice-status-badge :status="invoice.status" :due-date="invoice.due_date" />
                                                </td>
                                                <td class="text-left text-nowrap">
                                                    <div class="d-flex align-items-center">
                                                        <KvTooltip
                                                            v-if="canMarkPaid(invoice)"
                                                            :text="t('invoices-mark_as_paid')"
                                                        >
                                                            <kv-button
                                                                variant="text"
                                                                size="small"
                                                                prepend-icon="ui-check"
                                                                class="btn-pay-list"
                                                                @click.prevent="openPayModal(invoice)"
                                                            ></kv-button>
                                                        </KvTooltip>
                                                        <invoice-actions-menu
                                                            :invoice="invoice"
                                                            :isAdmin="true"
                                                            @view="goToDetails"
                                                            @delete="confirmDelete"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </template>
                                    <template v-slot:list="grid">
                                        <div class="row px-3 pt-2 mt-1">
                                            <div v-for="invoice in (grid.data || [])" :key="'m-' + invoice.id" class="col-12 mb-3 px-1">
                                                <KvCard class="invoice-mobile-card">
                                                    <template #text>
                                                    <div class="d-flex justify-content-between align-items-start mb-2">
                                                        <div class="d-flex flex-column">
                                                            <span class="invoice-id text-muted mb-1">#{{ invoice.id }}</span>
                                                            <h5 class="invoice-title-mobile mb-0 text-dark font-weight-500">{{ invoice.title || t('invoices-invoice') }}</h5>
                                                        </div>
                                                        <invoice-status-badge :status="invoice.status" :due-date="invoice.due_date" />
                                                    </div>

                                                    <div class="establishment-mobile mb-3">
                                                        <div class="text-muted small text-uppercase mb-1" style="letter-spacing: 0.05em; font-size: 10px;">{{ t('invoices-establishment') }}</div>
                                                        <div class="font-weight-500 text-dark">{{ invoice.place_name || `${t('invoices-establishment')} #${invoice.places_id}` }}</div>
                                                    </div>

                                                    <div class="info-row-mobile d-flex justify-content-between mb-3 gap-2 flex-wrap">
                                                        <div class="date-info">
                                                            <div class="text-muted small mb-1">{{ t('invoices-due_date') }}</div>
                                                            <div class="font-weight-500"><human-date :date="invoice.due_date" format="[d]/[M]/[Y]" /></div>
                                                        </div>
                                                        <div class="total-info text-right">
                                                            <div class="text-muted small mb-1">{{ t('invoices-total_amount') }}</div>
                                                            <div class="invoice-total-mobile mb-0 text-dark font-weight-500">{{ formatCurrency(invoice.total) }}</div>
                                                            <div v-if="invoice.total_paid !== null && Number(invoice.total_paid) > 0" class="paid-info-mobile small text-success mt-1">
                                                                {{ t('invoices-paid_label') }}: {{ formatCurrency(invoice.total_paid) }}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="card-mobile-actions border-top pt-3 mt-1 d-flex justify-content-between align-items-center">
                                                        <kv-button
                                                            v-if="canMarkPaid(invoice)"
                                                            variant="outlined"
                                                            size="small"
                                                            prepend-icon="ui-check"
                                                            @click.prevent="openPayModal(invoice)"
                                                        >
                                                            {{ t('invoices-mark_as_paid') }}
                                                        </kv-button>
                                                        <span v-else></span>
                                                        <invoice-actions-menu
                                                            :invoice="invoice"
                                                            :isAdmin="true"
                                                            @view="goToDetails"
                                                            @delete="confirmDelete"
                                                        />
                                                    </div>
                                                    </template>
                                                </KvCard>
                                            </div>
                                        </div>
                                    </template>
                                </grid>
                            </div>
                        </template>
                    </KvCard>
                </div>
            </div>
        </div>

        <KvModal
            ref="deleteModal"
            id="delete-invoice-modal"
            size="sm"
        >
            <template #title>{{ t('invoices-delete_confirm_title') }}</template>
            <template #subtitle v-if="deleteTarget">
                {{ deleteTarget.title || `${t('invoices-invoice')} #${deleteTarget.id}` }}
            </template>
            <template #body>
                {{ t('invoices-delete_confirmation') }}
            </template>
            <template #footer>
                <kv-button variant="ghost" @click="$refs.deleteModal.close()">{{ t('invoices-cancel') }}</kv-button>
                <kv-button variant="danger" @click="doDelete">{{ t('invoices-yes_delete') }}</kv-button>
            </template>
        </KvModal>

        <monthly-invoice-generation-modal
            ref="monthlyGenerationModal"
            :preview="monthlyGenerationPreview"
            :executing="monthlyGenerationExecuting"
            @execute="executeMonthlyGeneration"
        />

        <payment-confirm-modal
            ref="payModal"
            modal-id="invoices-grid-pay-modal"
            :invoice="payTarget || {}"
            :title="t('invoices-yes_mark_as_paid')"
            :subtitle="payTarget ? (payTarget.title || `${t('invoices-invoice')} #${payTarget.id}`) : ''"
            :hint="t('invoices-mark_as_paid_hint')"
            :loading="payLoading"
            :show-fines-fields="isPayTargetOverdue"
            @confirm="confirmPay"
            @close="onPayModalClose"
        />
    </div>
</template>

<script>
import keviApi from "../keviApi";
import saveData from "../helpers/saveData";
import KvFilter from "../components/reports/kv-filter-bar.vue";
import InvoiceSummaryCards from "./components/invoice-summary-cards.vue";
import InvoiceStatusBadge from "./components/invoice-status-badge.vue";
import InvoiceActionsMenu from "./components/invoice-actions-menu.vue";
import AddChargeForm from "./components/add-charge-form.vue";
import PaymentConfirmModal from "./components/payment-confirm-modal.vue";
import MonthlyInvoiceGenerationModal from "./components/monthly-invoice-generation-modal.vue";
import KvModal from "../components/modal/modal.vue";
import KvCard from "../components/ui/kv-card.vue";
import filtersMixin from "./helpers/filters-mixin";
import { formatCurrency, extractApiError, formatMonthYear } from "./helpers";
import { computeInvoiceStatus } from "./helpers";

const DEFAULT_SUMMARY = {
    total_invoices: 0,
    awaiting_payment_count: 0,
    awaiting_payment_value: 0,
    paid_count: 0,
    paid_value: 0,
    total_revenue: 0
};

export default {
    name: "invoices",
    mixins: [filtersMixin],
    components: {
        KvFilter,
        InvoiceSummaryCards,
        InvoiceStatusBadge,
        InvoiceActionsMenu,
        AddChargeForm,
        PaymentConfirmModal,
        MonthlyInvoiceGenerationModal,
        KvModal,
        KvCard
    },
    data() {
        return {
            summary:      { ...DEFAULT_SUMMARY },
            deleteTarget: null,
            payTarget:    null,
            payLoading:   false,
            exportLoading: false,
            monthlyGenerationPreview: null,
            monthlyGenerationLoading: false,
            monthlyGenerationExecuting: false,
            extraFilters: [
                {
                    component: "period",
                    label:     this.t('invoices-filter_by_date'),
                    name:      "period",
                    removable: false,
                    options:   { maxRangeMonths: 12 }
                },
                {
                    component: "checkbox",
                    label:     this.t('invoices-filter_status'),
                    name:      "status",
                    removable: true,
                    options: {
                        icon:    "fa-tag",
                        options: [
                            { value: "draft",     label: this.t('invoices-status_draft') },
                            { value: "pending",   label: this.t('invoices-status_pending') },
                            { value: "paid",      label: this.t('invoices-status_paid') },
                            { value: "overdue",   label: this.t('invoices-status_overdue_label') },
                            { value: "cancelled", label: this.t('invoices-status_cancelled') }
                        ]
                    }
                },
                {
                    component: "kv-select",
                    label:     this.t('invoices-filter_establishment'),
                    name:      "places_id",
                    removable: true,
                    options: {
                        options: []
                    }
                }
            ]
        };
    },
    computed: {
        isAdmin() {
            return !!this.$store?.state?.user?.global_admin;
        },
        isPayTargetOverdue() {
            if (!this.payTarget) return false;
            return computeInvoiceStatus(this.payTarget.status, this.payTarget.due_date) === 'overdue';
        }
    },
    created() {
        const permissions = this.$store.state.user?.permissions || {};
        const hasPermission = permissions.invoices?.enabled === 1;

        if (!hasPermission) {
            this.$router.replace('/');
            return;
        }
        this.loadEstablishments();
    },
    watch: {
        filters: {
            deep: true,
            immediate: true,
            handler: "loadSummary"
        }
    },
    methods: {
        async loadEstablishments() {
            try {
                const res = await this.$http(keviApi.getOptions('places', 'GET'));
                if (res && res.status === 200 && Array.isArray(res.body)) {
                    const places = res.body;
                    const placeFilter = this.extraFilters.find(f => f.name === 'places_id');
                    if (placeFilter) {
                        placeFilter.options.options = places.map(p => ({
                            value: p.id,
                            label: p.name
                        }));
                    }
                }
            } catch (err) {
                console.error("Error loading places:", err);
            }
        },
        async loadSummary() {
            try {
                const res = await this.$http(keviApi.getOptions("invoices/summary", "GET", {
                    filters: this.filters
                }));
                if (res && res.status === 200) {
                    this.summary = { ...DEFAULT_SUMMARY, ...res.body };
                }
            } catch (err) {
                console.error("Error loading summary:", err);
                this.summary = { ...DEFAULT_SUMMARY };
            }
        },
        formatCurrency,
        formatMonthYear,
        async openMonthlyGenerationPreview() {
            this.monthlyGenerationLoading = true;
            this.monthlyGenerationPreview = null;
            try {
                const response = await this.$http(keviApi.getOptions("invoices/generate-month", "POST", { dry_run: true }));
                if (response && response.status === 200) {
                    this.monthlyGenerationPreview = response.body;
                    this.$nextTick(() => this.$refs.monthlyGenerationModal && this.$refs.monthlyGenerationModal.open());
                } else {
                    const msg = extractApiError(response, this.t('invoices-generation_preview_error'));
                    this.$store.dispatch('notification/error', msg);
                }
            } catch (e) {
                const msg = extractApiError(e, this.t('invoices-generation_preview_error'));
                this.$store.dispatch('notification/error', msg);
            } finally {
                this.monthlyGenerationLoading = false;
            }
        },
        async executeMonthlyGeneration(selectedTargets = []) {
            this.monthlyGenerationExecuting = true;
            try {
                const response = await this.$http(keviApi.getOptions("invoices/generate-month", "POST", {
                    dry_run: false,
                    delete_drafts: true,
                    selected_targets: selectedTargets
                }));
                if (response && response.status === 200) {
                    this.monthlyGenerationPreview = response.body;
                    const summary = response.body.summary || {};
                    this.$store.dispatch(
                        response.body.status === 'success' ? 'notification/success' : 'notification/error',
                        this.t('invoices-generation_success', {
                            generated: summary.invoices_generated || 0,
                            deleted: summary.draft_invoices_deleted || 0
                        })
                    );
                    this.loadSummary();
                    this.$refs.invoicesGrid && this.$refs.invoicesGrid.update();
                    if (response.body.status === 'success') {
                        this.$refs.monthlyGenerationModal && this.$refs.monthlyGenerationModal.close();
                    }
                } else {
                    const msg = extractApiError(response, this.t('invoices-generation_execute_error'));
                    this.$store.dispatch('notification/error', msg);
                }
            } catch (e) {
                const msg = extractApiError(e, this.t('invoices-generation_execute_error'));
                this.$store.dispatch('notification/error', msg);
            } finally {
                this.monthlyGenerationExecuting = false;
            }
        },
        async exportInvoices() {
            this.exportLoading = true;
            try {
                const options = keviApi.getOptions('invoices/admin/export', 'GET', { filters: this.filters });
                const res = await this.$http(options);
                if (res.status === 200) {
                    const today = new Date().toISOString().slice(0, 10);
                    saveData(res.body, `faturas-export-${today}.csv`);
                    this.$store.dispatch('notification/success', this.t('invoices-export_success'));
                } else {
                    const msg = extractApiError(res, this.t('invoices-export_error'));
                    this.$store.dispatch('notification/error', msg);
                }
            } catch (e) {
                const msg = extractApiError(e, this.t('invoices-export_error'));
                this.$store.dispatch('notification/error', msg);
            } finally {
                this.exportLoading = false;
            }
        },
        canMarkPaid(invoice) {
            const computed = computeInvoiceStatus(invoice.status, invoice.due_date);
            return ['pending', 'overdue'].includes(computed);
        },
        openPayModal(invoice) {
            this.payTarget  = invoice;
            this.payLoading = false;
            this.$nextTick(() => this.$refs.payModal && this.$refs.payModal.open());
        },
        onPayModalClose() {
            this.payTarget  = null;
            this.payLoading = false;
        },
        async confirmPay({ interest_fines, total_paid }) {
            if (!this.payTarget) return;
            this.payLoading = true;
            try {
                const payload = { status: 'paid', interest_fines, total_paid };
                const res = await this.$http(keviApi.getOptions(`invoices/${this.payTarget.id}/status`, 'PATCH', payload));
                if (res.status === 200) {
                    this.$store.dispatch('notification/success', this.t('invoices-update_success'));
                    this.$refs.payModal && this.$refs.payModal.close();
                    this.loadSummary();
                    this.$refs.invoicesGrid && this.$refs.invoicesGrid.update();
                } else {
                    const msg = extractApiError(res, this.t('invoices-update_status_error'));
                    this.$store.dispatch('notification/error', msg);
                }
            } catch (e) {
                const msg = extractApiError(e, this.t('invoices-update_status_connection_error'));
                this.$store.dispatch('notification/error', msg);
            } finally {
                this.payLoading = false;
            }
        },
        goToDetails(invoice) {
            this.$router.push(`/invoices/${invoice.id}`);
        },
        confirmDelete(invoice) {
            if (invoice.status !== 'draft') {
                this.$store.dispatch('notification/error', this.t('invoices-delete_only_draft_error'));
                return;
            }
            this.deleteTarget = invoice;
            this.$refs.deleteModal.open();
        },

        async doDelete() {
            const invoice = this.deleteTarget;
            if (!invoice) return;

            try {
                const options = keviApi.getOptions(`invoices/${invoice.id}`, 'DELETE');
                const response = await this.$http(options);

                if (response.status === 200 || response.status === 204) {
                    this.$store.dispatch('notification/success', this.t('invoices-delete_success'));
                    this.loadSummary();
                    if (this.$refs.invoicesGrid) {
                        this.$refs.invoicesGrid.update();
                    }
                } else {
                    const msg = extractApiError(response, this.t('invoices-delete_error'));
                    this.$store.dispatch('notification/error', msg);
                }
            } catch (e) {
                console.error("Error deleting invoice:", e);
                const msg = extractApiError(e, this.t('invoices-delete_connection_error'));
                this.$store.dispatch('notification/error', msg);
            } finally {
                this.$refs.deleteModal && this.$refs.deleteModal.close();
                this.deleteTarget = null;
            }
        },
        onChargesCreated() {
            this.$refs.chargeModal.close();
            this.loadSummary();
            if (this.$refs.invoicesGrid) {
                this.$refs.invoicesGrid.update();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
#invoices-page {
    .invoices-filter-bar {
        background: transparent !important;
        border: none !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        
        ::v-deep .kv-filters {
            background: transparent !important;
            padding: 0 !important;
        }
    }

    .invoices-grid-card {
        border-radius: 12px;
        overflow: hidden;

        ::v-deep .kv-card-content-inner {
            padding: 0 !important;
        }
    }

    .invoices-actions-bar {
        gap: 8px;
    }

    .invoices-grid-wrapper {
        min-height: 300px;
        .text-nowrap {
            white-space: nowrap !important;
        }
        .est-col {
            min-width: 200px;
        }
    }

    .invoice-mobile-card {
        border-color: #E5E7EB !important;
        background-color: #FFF;
        transition: all 0.2s ease;

        ::v-deep .kv-card-content-inner {
            padding: 12px !important;
        }

        &:active {
            background-color: #F9FAFB;
            transform: scale(0.99);
        }

        .invoice-id {
            font-size: 11px;
            font-weight: 500;
            color: #6B7280;
            letter-spacing: 0.02em;
        }

        .invoice-title-mobile {
            font-size: 16px;
            color: #111827 !important;
        }

        .invoice-total-mobile {
            font-size: 18px;
            color: #111827;
        }

        .establishment-mobile {
            border-left: 2px solid #E5E7EB;
            padding-left: 12px;
            margin-left: 1px;
            
            .font-weight-500 {
                font-size: 15px;
                color: #374151;
            }
        }
        
        .paid-info-mobile {
            font-weight: 500;
            background: #F0FDF4;
            color: #15803D !important;
            padding: 2px 8px;
            border-radius: 6px;
            display: inline-block;
        }

        .card-mobile-actions {
            border-top: 1px solid #F3F4F6 !important;
            opacity: 0.8;
            
            ::v-deep .actions-group {
                width: fit-content;
                margin-left: auto;
            }
        }
    }

    .btn-pay-list {
        color: #059669 !important;
        &:hover {
            color: #047857 !important;
            background-color: #D1FAE5 !important;
        }
        ::v-deep .ui-icon { color: inherit !important; }
    }

}
</style>
